import React, { useState, useEffect } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useDispatch } from "react-redux";
import { Label } from "@progress/kendo-react-labels";
import { TextArea } from "@progress/kendo-react-inputs";
import styles from "../Casefile.module.scss";
import infoIcon from "../../../assets/CaseFileIcons/info-icon.png";
import { Button } from "@progress/kendo-react-buttons";
import {
  getDocument,
  saveActivity,
  updateMatterActivity,
  getMatterRates,
  getNewBlankDocument,
} from "api/index";
import {
  FormComboBox,
  FormInput,
  FormDate,
  FormNumericTextBox,
} from "components/Matters/index";
import { CONSTANT } from "CONSTANT/Matter/MatterActivityForm";
import MatterModal from "components/Matters/MatterModal";
import { filterBy } from "@progress/kendo-data-query";
import { useUploadForm } from "components/fileUploadHook";
import { onFileUpload } from "services/fileDataService";
import { PDFViewer } from "@progress/kendo-react-pdf-viewer";
import { Window } from "@progress/kendo-react-dialogs";
import { setMainLoading } from "actions/common.action";
import { FileDocumentTypes } from "components/Enums/FileDocumentTypes.ts";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Tile } from "components/UIComponents/Tile";
import { FileDragDrop } from "./CaseActivitiesPage";
import DocumentViewer from "components/common/DocumentViewer";
import { RiUpload2Fill } from "react-icons/ri";
import { toast } from "react-toastify";
import { useActiveFeeEarner } from "services/userService";
import { RxCross2 } from "react-icons/rx";
import { useActiveActivityTypes } from "services/activityTypeService";

const CaseActivityForm = (props) => {
  const { uploadForm } = useUploadForm();
  const [details, setDetails] = useState("");
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [rateInfoText, setRateInfoText] = useState(0);
  const [modalSuccess, setModalSuccess] = useState(undefined);
  const [newMaterId, setNewMatterId] = useState(undefined);
  const { data: activityTypes, loading: activityTypesLoading } =
    useActiveActivityTypes();
  const [viewFile, setViewFile] = useState("");
  const [pendingType, setPendingType] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isBilled, setIsBilled] = useState(false);
  const [inputState, setInputState] = useState({
    [CONSTANT.INPUT.DETAILS]: "",
    [CONSTANT.INPUT.UNITS]: !props.view ? 1 : 0,
    [CONSTANT.INPUT.MPU]: !props.view ? 6 : 0,
    [CONSTANT.INPUT.RATE]: 0.0,
    [CONSTANT.INPUT.VALUE]: 0.0,
  });

  const [comboBoxState, setComboBoxState] = useState({
    [CONSTANT.COMBOBOX.TYPE]: "",
    [CONSTANT.COMBOBOX.FEE_EARNER]: "",
  });

  const [dateState, setDateState] = useState({
    [CONSTANT.DATE.DATE]: new Date(),
  });
  const { data: feeEarnerData, loading } = useActiveFeeEarner();
  const [feeEarner, setFeeEarner] = useState([]);
  const [showPdf, setShowPdf] = useState(false);

  const filterFeeEarner = (filter) => {
    const data = feeEarnerData.slice();
    return filterBy(data, filter);
  };
  const feeEarnerfilterChange = (event) => {
    setFeeEarner(filterFeeEarner(event.filter));
  };

  const normalizedProps = React.useMemo(() => {
    if (props.isView && props.viewActivity) {
      return {
        // View/Edit mode props
        feeEarnerRef: props.viewActivity.feeEarnerRef,
        document: {
          id: props.viewActivity.documentId,
          file: props.viewActivity.documentFile,
        },
        isBilled: props.viewActivity.isBilled || false,
        details: props.viewActivity.details,
        units: props.viewActivity?.units,
        mpu: props.viewActivity?.mpu || 6,
        rate: props.viewActivity?.rate || 0,
        value: props.viewActivity?.value || 0,
        type: props.viewActivity.type,
        typeId: props.viewActivity.typeId,
        date: new Date(props.viewActivity.date),
        isDuplicate: props.isDuplicate || false,
        mode: "view",
      };
    }
    return {
      feeEarnerRef: props.feeEarnerRef,
      document: {
        files: props.droppedFilesActivity,
      },
      isBilled: false,
      details: "",
      units: 1,
      mpu: 6,
      rate: 0,
      value: 0,
      type: "",
      typeId: null,
      date: new Date(),
      isDuplicate: false,
      mode: "add",
    };
  }, [
    props.isView,
    props.viewActivity,
    props.feeEarnerRef,
    props.droppedFilesActivity,
  ]);

  const initializeForm = React.useCallback(() => {
    if (!feeEarnerData || loading) return;

    setFeeEarner(feeEarnerData);
    // Set form values based on mode
    setIsBilled(normalizedProps.isBilled);
    setDetails(normalizedProps.details);
    setInputState({
      [CONSTANT.INPUT.UNITS]: normalizedProps.units,
      [CONSTANT.INPUT.MPU]: normalizedProps.mpu,
      [CONSTANT.INPUT.RATE]: normalizedProps.rate,
      [CONSTANT.INPUT.VALUE]: normalizedProps.value,
    });
    const selectedFeeEarner = feeEarnerData.find(
      (item) => item.userRef === normalizedProps.feeEarnerRef
    );

    if (selectedFeeEarner) {
      setComboBoxState((prev) => ({
        ...prev,
        [CONSTANT.COMBOBOX.FEE_EARNER]: {
          display: selectedFeeEarner.display,
          userRef: selectedFeeEarner.userRef,
        },
        [CONSTANT.COMBOBOX.TYPE]: {
          name: normalizedProps.type,
          activityTypeID: normalizedProps.typeId,
        },
      }));
    }
    setDateState({
      [CONSTANT.DATE.DATE]: normalizedProps.date,
    });

    // Handle document if in view mode
    if (normalizedProps.mode === "view" && normalizedProps.document.id) {
      loadDocument(normalizedProps.document);
    }
  }, [loading, normalizedProps]);

  const loadDocument = async (document) => {
    try {
      let fileData;

      if (normalizedProps.isDuplicate && document.file) {
        fileData = document.file;
      } else if (document.id) {
        const res = await getDocuemntData(document.id);
        fileData = res.data?.data?.documentFile;
      } else if (document.file?.downloadURL) {
        fileData = document.file;
      }

      if (fileData) {
        setFilesData([fileData]);
        setFileId(fileData.fileID);

        if (!normalizedProps.isDuplicate && fileData.downloadURL) {
          const response = await fetch(fileData.downloadURL);
          const blob = await response.blob();
          const base64data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          setViewFile(base64data.split(",")[1]);
        }
      }
    } catch (error) {
      console.error("Failed to load document:", error);
    }
  };

  useEffect(() => {
    if (feeEarnerData && !loading) {
      initializeForm();
    }
  }, [loading]);

  useEffect(() => {
    if (props.droppedFilesActivity?.length && normalizedProps.mode === "add") {
      if (props.component === "DOC-TEMPLATE") {
        handleDocTemplate(props.droppedFilesActivity[0]);
      } else {
        handleUpload({
          target: {
            files: props.droppedFilesActivity,
          },
        });
      }
    }
  }, [props.droppedFilesActivity]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parseDate = (newDate) => {
      if (!newDate) return null;
      var os = newDate?.getTimezoneOffset();
      return (newDate = new Date(newDate.getTime() - os * 60 * 1000));
    };

    const dataObject = {
      typeId: comboBoxState[CONSTANT.COMBOBOX.TYPE].activityTypeID || null,
      date: parseDate(dateState[CONSTANT.DATE.DATE]) || null,
      details: details,
      feeEarnerRef: comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER].userRef || null,
      units: parseInt(inputState[CONSTANT.INPUT.UNITS]) || null,
      mpu: parseInt(inputState[CONSTANT.INPUT.MPU]) || null,
      rate: parseFloat(inputState[CONSTANT.INPUT.RATE]) || null,
      value: parseFloat(inputState[CONSTANT.INPUT.VALUE]) || null,
      fileId: fileId.length ? fileId : null,
      subType: "None",
    };
    if (dataObject.details === "" || dataObject.feeEarnerRef === null) {
      setError(true);
      return;
    }
    dispatch(setMainLoading(true));
    if (props.isView && !props?.isDuplicate) {
      try {
        const res = await updateMatterActivity(
          props.viewActivity.matterId,
          props.viewActivity.activityId,
          dataObject
        );

        if (res?.status === 200) {
          dispatch(setMainLoading(false));
          // updateModalState(true, "Matter Activity Updated Successfully", true);
          toast.success("Activity Updated Sucessfully");
          props.getActivitiesData();
          props.setshowMatterForm(false);
        }
        return;
      } catch (err) {
        if (
          err?.response?.data?.errorMessage.includes(
            "The duplicate key value is"
          )
        ) {
          dispatch(setMainLoading(false));
          toast.error("Error occured adding activity.");
        } else {
          dispatch(setMainLoading(false));
          // updateModalState(true, "Error! Matter Update Failed", false);
          toast.error("Error occured adding activity.");
        }
      }
    } else {
      try {
        const res = await saveActivity(props.reference, dataObject);
        if (res?.status === 200) {
          dispatch(setMainLoading(false));
          toast.success("Activity Added Sucessfully");
          props.getActivitiesData();
          props.setshowMatterForm(false);
          setNewMatterId(res.data.data.matterId);
        }
      } catch (err) {
        if (
          err?.response?.data?.errorMessage?.includes(
            "The duplicate key value is"
          )
        ) {
          dispatch(setMainLoading(false));
          // updateModalState(true, "Matter Activity Already Added", false);
          toast.success("Error occured during adding");
        } else {
          dispatch(setMainLoading(false));
          // updateModalState(true, "Error Occured", false);
          toast.success("Error occured during adding");
        }
      }
    }
    setError(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const updateModalState = (modalShow, message, success) => {
    setShowModal(modalShow);
    setModalMessage(message);
    setModalSuccess(success);
  };

  const getDocuemntData = async (id) => {
    try {
      if (id) {
        const { data } = await getDocument(id);
        setFilesData([data?.data?.documentFile]);
      }
    } catch (error) {}
  };

  const handleInputChange = (value, label) => {
    setInputState({
      ...inputState,
      [label]: value,
    });
  };

  const handleComboBoxChange = (e, label) => {
    setComboBoxState({
      ...comboBoxState,
      [label]: e.value,
    });
  };

  const handleDrop = (event) => {
    if (!props.docId?.documentId) {
      handleUpload({
        target: {
          files: [...event],
        },
      });
    }
  };

  const handleDateChange = (e, label) => {
    setDateState({
      ...dateState,
      [label]: e.value,
    });
  };

  const handleTypeChange = (value) => {
    setComboBoxState({
      ...comboBoxState,
      [CONSTANT.COMBOBOX.TYPE]: value.value,
    });
  };
  const [filesData, setFilesData] = useState([]);
  const [fileId, setFileId] = useState([]);
  const filePickerRef = React.useRef();
  const uploadRef = React.createRef();

  const handleUpload = async (e) => {
    try {
      setIsFileLoading(true);
      let data = onFileUpload(e);

      const dataChoosen = await uploadForm(data, e.target.files[0], {
        documentType: FileDocumentTypes.CaseFile,
        matterId: props.reference,
      });

      data.fileID = dataChoosen.data.data.fileID;
      setFileId(data.fileID);
      setFilesData([{ ...dataChoosen?.data?.data }]);

      const fileExt = dataChoosen?.data?.data?.filename
        ?.split(".")
        .pop()
        ?.toLowerCase();

      if (fileExt === "eml" || fileExt === "msg") {
        const emailType = activityTypes.find((item) => item.code === "EO");
        if (emailType) {
          setComboBoxState((prev) => ({
            ...prev,
            [CONSTANT.COMBOBOX.TYPE]: emailType,
          }));
        }
        if (dataChoosen?.data?.data?.subject) {
          setDetails(dataChoosen.data.data.subject);
        }
      } else if (fileExt === "doc" || fileExt === "docx") {
        const docType = activityTypes.find((item) =>
          item.name?.toLowerCase().includes("document")
        );
        if (docType) {
          setComboBoxState((prev) => ({
            ...prev,
            [CONSTANT.COMBOBOX.TYPE]: docType,
          }));
        }
      }
    } catch (error) {
      console.error("File upload failed:", error);
      toast.error("Error occurred during file upload.");
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleDocTemplate = async (data) => {
    try {
      setIsFileLoading(true);
      setFileId(data.fileID);
      setFilesData([
        {
          ...data,
        },
      ]);
      switch (data?.filename?.split(".").pop()) {
        case "eml":
        case "msg":
          setComboBoxState({
            ...comboBoxState,
            [CONSTANT.COMBOBOX.TYPE]:
              activityTypes.filter((item) => item.code === "EO")[0] || null,
          });
          break;
        case "doc":
        case "docx":
          setComboBoxState({
            ...comboBoxState,
            [CONSTANT.COMBOBOX.TYPE]:
              activityTypes.filter((item) =>
                item.name?.toLowerCase()?.includes("document")
              )[0] || null,
          });
          break;
        default:
          break;
      }
    } catch (error) {
    } finally {
      setIsFileLoading(false);
    }
  };
  const onFileRemove = (fileId) => {
    setFilesData((prev) => prev.filter((file) => file.fileID !== fileId));
  };

  const handleNewDocument = async () => {
    try {
      setIsFileLoading(true);
      const data = {
        clientId: props.matterDetails?.clientId || 0,
        matterId: props.reference,
        userRef: props.feeEarnerRef || "",
        supplierRef: "",
        nominalRef: "",
        documentType: 1,
      };
      const response = await getNewBlankDocument({ data });
      const fileData = response.data.data;

      setFileId(fileData.fileID);
      setFilesData([
        {
          ...fileData,
        },
      ]);

      // Set activity type to Document if available
      const documentActivityType = activityTypes.filter((item) =>
        item.name?.toLowerCase()?.includes("document")
      )[0];

      if (documentActivityType) {
        setComboBoxState({
          ...comboBoxState,
          [CONSTANT.COMBOBOX.TYPE]: documentActivityType,
        });
      }

      toast.success("New document created successfully");
    } catch (error) {
      toast.error("Error creating new document");
      console.error("Error creating new document:", error);
    } finally {
      setIsFileLoading(false);
    }
  };

  const getMatterRateValue = async () => {
    try {
      const data = await getMatterRates(
        props.reference,
        comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER]?.userRef
      );
      setInputState({
        ...inputState,
        [CONSTANT.INPUT.RATE]: data?.data?.data?.rateValue,
      });
      setRateInfoText(data?.data?.data?.rateInfo);
    } catch (error) {}
  };
  const calculateValue = () => {
    if (
      inputState[CONSTANT.INPUT.RATE] &&
      inputState[CONSTANT.INPUT.UNITS] &&
      inputState[CONSTANT.INPUT.MPU]
    ) {
      setInputState((prevState) => {
        const value =
          (prevState[CONSTANT.INPUT.RATE] / 60) *
          (prevState[CONSTANT.INPUT.UNITS] * prevState[CONSTANT.INPUT.MPU]);
        const newState = {
          ...prevState,
          [CONSTANT.INPUT.VALUE]: value,
        };
        return newState;
      });
    }
  };
  React.useEffect(() => {
    calculateValue();
  }, [
    inputState[CONSTANT.INPUT.RATE],
    inputState[CONSTANT.INPUT.UNITS],
    inputState[CONSTANT.INPUT.MPU],
  ]);

  React.useEffect(() => {
    if (comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER] !== "" && !props.isView)
      getMatterRateValue();
  }, [comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER]]);

  return (
    <div className={styles["matter-form-dialog"]}>
      <Dialog
        title={
          props.isView && !props?.isDuplicate
            ? "Update Activity"
            : "Add Activity"
        }
        onClose={props.setshowMatterForm}
        className="dialog-lg"
      >
        <div className="d-flex flex-column w-100 h-100">
          <form
            id="matterForm"
            onSubmit={handleSubmit}
            className="flex-grow-1 h-100"
          >
            <div className="row g-0 h-100">
              <div className="bgForm py-4 px-5 col-12 col-lg-4 d-flex flex-column">
                <div className="">
                  <div className="">
                    <span className={`${styles["add-activity-form-text"]}`}>
                      {props.addFormText}
                    </span>
                  </div>
                  <FormComboBox
                    // required={true}
                    isStyled={true}
                    className="mt-4"
                    width={"260px"}
                    wf={true}
                    name={CONSTANT.COMBOBOX.TYPE}
                    styles={{
                      border:
                        error === true &&
                        !comboBoxState[CONSTANT.COMBOBOX.TYPE] &&
                        "1px solid red",
                    }}
                    textField={"name"}
                    state={comboBoxState}
                    data={activityTypes}
                    val={comboBoxState[CONSTANT.COMBOBOX.TYPE]}
                    onChangeComboBox={handleTypeChange}
                  />

                  <FormDate
                    isStyled={true}
                    className="note-date-picker mt-3"
                    label={CONSTANT.DATE.DATE}
                    onChangeDate={handleDateChange}
                    state={dateState}
                  />

                  <FormComboBox
                    // required={true}
                    loading={loading}
                    isStyled={true}
                    className="mt-4"
                    width={"260px"}
                    wf={true}
                    styles={{
                      border:
                        error === true &&
                        !comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER] &&
                        "1px solid red",
                    }}
                    onChangeComboBox={handleComboBoxChange}
                    onFilterChange={feeEarnerfilterChange}
                    data={feeEarner}
                    textField="display"
                    val={comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER]}
                    state={comboBoxState}
                    name={CONSTANT.COMBOBOX.FEE_EARNER}
                  />
                  <div className="col-12 d-flex flex-column mt-4">
                    <Label className={`label`}>Details</Label>
                    <TextArea
                      // required
                      placeholder="Enter Details"
                      autoSize={true}
                      value={details}
                      style={{
                        height: "92px",
                        width: "260px",
                        border: error === true && !details && "1px solid red",
                      }}
                      onChange={(e) => setDetails(e.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-8 d-flex flex-column">
                <Tile
                  heading={`Time Record ${isBilled ? "(Billed)" : ""}`}
                  className="m-3 p-3 ps-4 pe-4 pt-2 pb-0 d-flex flex-column flex-grow-0 flex-shrink-0"
                  height={280}
                >
                  <div className="ps-2 pb-4 flex-shrink-0">
                    <FormNumericTextBox
                      type="number"
                      width="286px"
                      state={inputState}
                      steppers={true}
                      format=""
                      className={"mt-2 col-4"}
                      labelForBox={"Units"}
                      label={CONSTANT.INPUT.UNITS}
                      onChangeInput={handleInputChange}
                      disabled={isBilled}
                    />

                    <div className="d-flex align-items-center mt-2 col-4 gap-1">
                      <div className="d-flex flex-column right-align">
                        <div className="d-flex align-items-baseline ">
                          <Label className="label mb-1 ">Rate</Label>
                          <Tooltip anchorElement="target" position="top">
                            <img
                              className="ms-1 cursor-pointer"
                              src={infoIcon}
                              title={rateInfoText}
                              style={{
                                height: "14px",
                                width: "14px",
                              }}
                              alt="info"
                            />
                          </Tooltip>
                        </div>

                        <FormInput
                          type="number"
                          width="286px"
                          labelShow={false}
                          state={inputState}
                          className={"col-4"}
                          label={CONSTANT.INPUT.RATE}
                          onChangeInput={handleInputChange}
                          disabled={isBilled}
                        />
                      </div>
                      <div></div>
                    </div>
                    <div className="row mt-2">
                      <div className="d-flex align-items-baseline ">
                        <Label className="label mb-1 ">Value</Label>
                        <Tooltip anchorElement="target" position="top">
                          <img
                            className="ms-1 cursor-pointer"
                            src={infoIcon}
                            title={`MPU = ${inputState[CONSTANT.INPUT.MPU]}`}
                            style={{
                              height: "14px",
                              width: "14px",
                            }}
                            alt="info"
                          />
                        </Tooltip>
                      </div>
                      <FormInput
                        type="number"
                        width="286px"
                        labelShow={false}
                        state={inputState}
                        className={"col-4"}
                        label={CONSTANT.INPUT.VALUE}
                        onChangeInput={handleInputChange}
                        disabled={isBilled}
                      />
                    </div>
                  </div>
                </Tile>
                <FileDragDrop onFilesDrop={handleDrop} uploadRef={uploadRef}>
                  <Tile
                    heading="Document"
                    className="p-3 mx-3 ps-4 pe-4 mb-3 d-flex flex-column flex-grow-1"
                    height={410}
                    headingItems={
                      props.isAddActivityBtn && (
                        <div className="d-flex gap-1 justify-content-center align-items-center">
                          {filesData.length >= 1 && (
                            <div
                              className={` rounded cursor-pointer ${styles["cross-icon"]}`}
                              onClick={() => {
                                if (!props.isView && filesData.length) {
                                  setFilesData([]);
                                } else {
                                  toast.error(
                                    "Attached document cant be changed"
                                  );
                                }
                              }}
                            >
                              <RxCross2 size={20} color="red" />
                            </div>
                          )}
                        </div>
                      )
                    }
                  >
                    {filesData[0] ? (
                      <div className="mt-2 flex-grow-1 overflow-auto">
                        <DocumentViewer
                          data={filesData}
                          onScreenComponent={true}
                          actionBar={false}
                          setDetails={setDetails}
                        />
                      </div>
                    ) : (
                      <div
                        onClick={(e) => filePickerRef?.current?.click()}
                        className={`d-flex align-items-center cursor-pointer justify-content-center flex-grow-1`}
                      >
                        {isFileLoading ? (
                          <div>
                            <div
                              className="spinner-border spinner"
                              role="status"
                              // style={{ marginRight: "60px" }}
                            ></div>
                          </div>
                        ) : (
                          <>
                            <div className="mt-3">
                              <p className={styles["doc-upload-text"]}>
                                {filesData[0]?.downloadURL
                                  ? "Update"
                                  : "Attach"}
                                <br /> Documents
                              </p>
                            </div>
                            <div className={styles["vertical-rule"]}></div>
                            <div
                              className={`${styles["file-upload-icon"]} d-flex align-items-center justify-content-center position-relative`}
                            >
                              <RiUpload2Fill color="#DCDAFF" size={20} />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Tile>
                </FileDragDrop>
              </div>
            </div>
          </form>
          <input
            name="file"
            type="file"
            onChange={handleUpload}
            ref={filePickerRef}
            style={{
              visibility: "hidden",
            }}
          />
          </div>
        <DialogActionsBar>
          <div className="dialog-footer d-flex flex-wrap justify-content-end gap-2 me-3">
            <Button
              className="common-btn bgBtnSecondary border-0"
              onClick={props.setshowMatterForm}
            >
              Cancel
            </Button>
            <Button className="common-btn" type="submit" form="matterForm">
              {props.isView && !props?.isDuplicate ? "Update" : "Save"}
            </Button>
          </div>
        </DialogActionsBar>

        {showPdf && (
          <Window
            className="responsive-window"
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              zIndex: 999999,
            }}
            title="View"
            onClose={() => setShowPdf(false)}
            initialWidth={"90%"}
            initialHeight={"80vh"}
          >
            <PDFViewer
              data={viewFile}
              onClose={(e) => {
                setViewFile("");
              }}
              style={{
                height: "100%",
                width: "100%",
              }}
            />
          </Window>
        )}
      </Dialog>

      <MatterModal
        showModal={showModal}
        message={modalMessage}
        success={modalSuccess}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default CaseActivityForm;
